import StudentInputSettings from "../../../shared-components/StudentInputSettings";
import { CreateWithBudButton } from "../../assistant/components/CreateWithBudButton";
import React, { useEffect, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { withPageTracking } from "../../../services/postHog";
import { AddDetails } from "../../../shared-components/workflows/AddDetails";
import { useApiClient } from "../../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { AddQuestions } from "../../../shared-components/workflows/AddQuestions";
import { enqueueSnackbar } from "notistack";
import { useUpdateAssignmentPreferences } from "../hooks/useUpdateAssignmentPreferences";
import {
  Card,
  CardContent,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip,
} from "@mui/material";
import { DEFAULT_PREFS, MARKING_MODES } from "../../../utils/constants";
import { isHolisticMarking } from "../../../utils/preferences";
import {
  BetaFeatureChip,
  useBetaAccess,
} from "../../../shared-components/BetaFeature";
import { checkIfIsIndependentUser } from "../../auth/Utils";

export const ManagePreferences = ({ assignment }) => {
  const ApiClient = useApiClient();
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [pendingPrefs, setPendingPrefs] = useState(null);
  const betaAccess = useBetaAccess();
  const { updatePreferences, saving } = useUpdateAssignmentPreferences({
    assignment: assignment,
  });

  useEffect(() => {
    if (assignment?.id) {
      setDefaultsLoading(false);
      return;
    }
    let active = true;
    ApiClient.users
      .getUserPreferences()
      .then((response) => {
        if (active && !assignment?.id) {
          const defaults = JSON.parse(response.data || "{}");
          setPrefs((previous) => ({
            ...previous,
            allow_spelling_assistance:
              defaults.allow_spelling_assistance !== false,
            allow_copy_paste: defaults.allow_copy_paste !== false,
          }));
        }
        if (active) setDefaultsLoading(false);
      })
      .catch(() =>
        enqueueSnackbar(
          "Unable to load teacher defaults. Please reopen assignment creation.",
          { variant: "error" },
        ),
      );
    return () => {
      active = false;
    };
  }, [ApiClient, assignment?.id]);

  const handleChange = (key, value) => {
    const updatedPrefs = { ...prefs, [key]: value };
    setPrefs(updatedPrefs);
    if (!assignment?.id) {
      setPendingPrefs(updatedPrefs);
      return;
    }
    updatePreferences({
      prefs: updatedPrefs,
      onSuccess: (newPrefs) => setPrefs(newPrefs),
    });
  };

  useEffect(() => {
    if (assignment?.preferences && !pendingPrefs) {
      const parsed = JSON.parse(assignment?.preferences?.data);

      const sanitized = {
        allow_spelling_assistance: parsed.allow_spelling_assistance !== false,
        allow_copy_paste: parsed.allow_copy_paste !== false,
        hide_marks: Boolean(parsed.hide_marks), // Explicit cast
        marking_mode: parsed.marking_mode || MARKING_MODES.ANALYTIC,
        auto_release_marks: Boolean(parsed.auto_release_marks),
        notify_students_by_email_of_feedback: Boolean(
          parsed.notify_students_by_email_of_feedback,
        ),
      };

      setPrefs(sanitized);
    }
  }, [assignment?.id, assignment?.preferences?.data]);

  useEffect(() => {
    if (!assignment?.id || !pendingPrefs) return;

    updatePreferences({
      prefs: pendingPrefs,
      onSuccess: (newPrefs) => {
        setPrefs(newPrefs);
        setPendingPrefs(null);
      },
    });
  }, [assignment?.id, pendingPrefs]);

  const disabled = saving || defaultsLoading;
  return (
    <Card>
      <CardContent>
        <Typography variant={"subtitle1"}>Preferences</Typography>
        <Typography variant={"body2"}>
          These can be changed in the assignment settings after creation
        </Typography>
        <StudentInputSettings
          prefs={prefs}
          onChange={handleChange}
          disabled={disabled}
        />
        <Stack direction={"row"} spacing={2}>
          <Tooltip
            title={
              "Automatically release marks to students after Fruitify marks it"
            }
          >
            <FormControlLabel
              control={
                <Switch
                  disabled={disabled}
                  checked={prefs.auto_release_marks}
                  onChange={(e) =>
                    handleChange("auto_release_marks", e.target.checked)
                  }
                />
              }
              label="Auto release marks"
            />
          </Tooltip>
          <Tooltip
            title={"Send students an email when their feedback is released"}
          >
            <FormControlLabel
              control={
                <Switch
                  disabled={disabled}
                  checked={prefs.notify_students_by_email_of_feedback}
                  onChange={(e) =>
                    handleChange(
                      "notify_students_by_email_of_feedback",
                      e.target.checked,
                    )
                  }
                />
              }
              label="Notify students of feedback"
            />
          </Tooltip>
          {betaAccess && (
            <Tooltip
              title={
                "Mark the answers holistically (This is still in beta testing)"
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    disabled={disabled}
                    checked={isHolisticMarking(prefs)}
                    onChange={(e) =>
                      handleChange(
                        "marking_mode",
                        e.target.checked
                          ? MARKING_MODES.HOLISTIC
                          : MARKING_MODES.ANALYTIC,
                      )
                    }
                  />
                }
                label="Holistic Marking"
              />
              <BetaFeatureChip />
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const AssignmentCreateNew = ({
  existingAssignment = null,
  onPageRefresh = null,
}) => {
  const [schoolClass, setSchoolClass] = useState(null);
  const [assignmentQuestions, setAssignmentQuestions] = useState([]);
  const [assignmentDetails, setAssignmentDetails] = useState({});
  const navigate = useNavigate();
  const isIndependentUser = checkIfIsIndependentUser();
  const ApiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get("classId");
  const questionId = searchParams.get("questionId");
  const createGroupWithName = searchParams.get("createGroupWithName");

  // If createGroupWithName exists, we create a group
  // The value is the group name (or empty string to use assignment name)
  const createAssignmentGroup = Boolean(createGroupWithName);
  const groupName = createGroupWithName || null;

  const detailsComplete = Boolean(
    assignmentDetails.name?.trim() &&
      assignmentDetails.subject &&
      assignmentDetails.educationLevel &&
      assignmentDetails.yearGroup &&
      assignmentDetails.dueDate,
  );

  const fetchClass = async () => {
    try {
      const response = await ApiClient.schools.getSchoolClassData({
        schoolClass: classId,
      });
      if (response) {
        setSchoolClass(response.schoolClass);
        setAssignmentDetails({
          id: "",
          name: "",
          schoolClass: response.schoolClass,
          subject: response.schoolClass.subject,
          educationLevel: response.schoolClass.educationLevel,
          yearGroup: response.schoolClass.yearGroup,
          dueDate: dayjs(),
        });
      }
    } catch (error) {
      console.error("Failed to fetch question", error);
    }
  };

  useEffect(() => {
    if (classId) {
      fetchClass();
    }
  }, [classId]);

  useEffect(() => {
    if (questionId && detailsComplete) {
      handleAddQuestionsToAssignment([{ id: questionId }]);
    }
  }, [questionId, detailsComplete]);

  useEffect(() => {
    if (existingAssignment) {
      setSchoolClass(existingAssignment.schoolClass);
      setAssignmentDetails({
        id: existingAssignment.id,
        name: existingAssignment.name,
        schoolClass: existingAssignment.schoolClass,
        subject: existingAssignment.schoolClass.subject,
        educationLevel: existingAssignment.schoolClass.yearGroup.educationLevel,
        yearGroup: existingAssignment.schoolClass.yearGroup,
        dueDate: dayjs(existingAssignment.dueDate),
        preferences: existingAssignment.preferences,
      });
      if (Array.isArray(existingAssignment?.assignmentQuestions)) {
        const questions = existingAssignment.assignmentQuestions.map(
          (aq) => aq.question,
        );
        setAssignmentQuestions(questions);
      }
    }
  }, [existingAssignment]);

  const handleAssignmentDetailsChange = (details) => {
    setAssignmentDetails(details);
  };

  const handleAddQuestionsToAssignment = async (
    questions,
    newDetails = null,
  ) => {
    try {
      const details = newDetails ?? assignmentDetails;
      const allQuestionIds = [
        ...questions.map((q) => q.id), // new questions
        ...(assignmentQuestions?.map((qq) => qq.id) || []), // existing quizQuestions
      ];
      // Remove duplicates
      const uniqueQuestionIds = Array.from(new Set(allQuestionIds));
      const response = await ApiClient.assignment.createAssignment({
        assignment: details.id,
        name: details.name,
        dueDate: dayjs(details.dueDate).format("YYYY-MM-DD"),
        schoolClass: details.schoolClass?.id,
        questions: uniqueQuestionIds,
        createAssignmentGroup,
        groupName: groupName || undefined,
      });
      if (response.success) {
        if (details?.id === response?.assignment.id) {
          // Updating existing assignment
          if (onPageRefresh) {
            // If callback provided (embedded mode), refresh the page
            onPageRefresh();
          } else {
            // If no callback (standalone mode), update local state
            setAssignmentQuestions((prev) => [...prev, ...questions]);
          }
        } else {
          setAssignmentDetails((prev) => ({
            ...prev,
            id: response.assignment.id,
            preferences: response.preferences,
          }));
          setAssignmentQuestions(questions);

          if (!createAssignmentGroup) {
            navigate(`/assignments/view/${response.assignment.id}`);
          }
        }
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error submitting, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };
  const handleRemoveQuestionsFromAssignment = async (question) => {
    try {
      const response = await ApiClient.assignment.removeQuestion({
        assignment: assignmentDetails.id,
        question: question.id,
      });
      if (response.success) {
        if (onPageRefresh) {
          onPageRefresh();
        } else {
          // If no refresh callback, update local state
          setAssignmentQuestions((prev) =>
            prev.filter((q) => q.id !== question.id),
          );
        }
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error removing question, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleSchoolClassChange = (schoolClass) => {
    setSchoolClass(schoolClass);
    setAssignmentDetails((prev) => {
      if (!schoolClass) {
        return {
          ...prev,
          schoolClass: null,
          subject: null,
          educationLevel: null,
          yearGroup: null,
        };
      }

      return {
        ...prev,
        schoolClass: schoolClass,
        subject: schoolClass.subject,
        educationLevel: schoolClass.educationLevel,
        yearGroup: schoolClass.yearGroup,
      };
    });
  };

  const handlePublishAssignment = async () => {
    try {
      const response = await ApiClient.assignment.publishAssignment({
        assignment: assignmentDetails.id,
      });
      if (response.success) {
        // If creating an assignment group, redirect to assignment groups overview
        if (createAssignmentGroup) {
          navigate("/assignments/groups");
        } else if (onPageRefresh) {
          // Otherwise refresh the page if callback is provided
          onPageRefresh();
        }
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar("Error publishing assignment, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };
  return (
    <FruitifyContainer>
      <AddDetails
        renderAdditionalActions={(onOpened) => (
          <CreateWithBudButton
            kind="assignment"
            details={assignmentDetails}
            unsaved={true}
            onOpened={onOpened}
          />
        )}
        details={assignmentDetails}
        onChange={handleAssignmentDetailsChange}
        schoolClass={schoolClass}
        onSchoolClassChange={handleSchoolClassChange}
        onComplete={handlePublishAssignment}
        canBePublished={detailsComplete && assignmentQuestions?.length > 0}
        assignment
      />
      {!isIndependentUser && (
        <ManagePreferences assignment={assignmentDetails} />
      )}
      {schoolClass && (
        <AddQuestions
          details={assignmentDetails}
          disabled={!detailsComplete}
          questions={assignmentQuestions}
          onRemoveQuestions={handleRemoveQuestionsFromAssignment}
          onAddQuestions={handleAddQuestionsToAssignment}
          ongoingJobs={existingAssignment?.ongoingJobs || []} // TODO: fetch this on the view
          onRefresh={onPageRefresh}
          assignment
        />
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentCreateNew, {
  feature: "assignment-create-new",
});
